import { useState, useEffect, useRef } from "react";
import {
  BookOpen,
  Save,
  Eye,
  AlertCircle,
  CheckCircle,
  Loader,
  Search,
  User,
  Calendar,
  Shield,
  Building,
  Car,
  Umbrella,
  Briefcase,
  Users,
  GraduationCap,
  Crown,
  Ship,
  Plane,
  FileText,
  HardHat
} from "lucide-react";
import JoditEditor from "jodit-react";
import {
  getLOBs,
  getInterpretationGuideContent,
  saveInterpretationGuideContent,
  kebabToTitle,
  kebabToSnake,
  getLOBIcon
} from "../../api/interpretationGuideService";

export function InterpretationGuide () {
  // Select LOB and load its content
  const handleLobSelect = async lob => {
    setSelectedLob(lob);
    setSelectedLobSnake(kebabToSnake(lob));
    setContentLoading(true);
    try {
      const guideContent = await getInterpretationGuideContent(
        kebabToSnake(lob)
      );
      setContent(guideContent.html || "");
    } catch (error) {
      setNotification({
        type: "error",
        message: "Failed to load guide content."
      });
      setContent("");
    } finally {
      setContentLoading(false);
    }
  };

  // Preview mode toggle
  const handlePreview = () => {
    setShowPreview(prev => !prev);
  };

  // Save guide content
  const handleSave = async () => {
    setSaving(true);
    try {
      console.log("Saving content:", content);
      await saveInterpretationGuideContent(selectedLobSnake, content);
      setNotification({
        type: "success",
        message: "Guide saved successfully."
      });
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } catch (error) {
      setNotification({ type: "error", message: "Failed to save guide." });
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } finally {
      setSaving(false);
    }
  };
  const editor = useRef(null);
  const [lobs, setLobs] = useState([]); // Array of kebab-case LOB names
  const [filteredLobs, setFilteredLobs] = useState([]);
  const [selectedLob, setSelectedLob] = useState(null); // kebab-case LOB name
  const [selectedLobSnake, setSelectedLobSnake] = useState(null); // snake_case for API
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [notification, setNotification] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [headerHeight, setHeaderHeight] = useState(80); // Default header height

  // Calculate header height dynamically
  useEffect(() => {
    const calculateHeaderHeight = () => {
      const header = document.querySelector("header");
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    calculateHeaderHeight();
    window.addEventListener("resize", calculateHeaderHeight);

    return () => window.removeEventListener("resize", calculateHeaderHeight);
  }, []);

  // Icon mapping for dynamic icons
  const iconComponents = {
    Shield,
    Building,
    Car,
    HardHat,
    Umbrella,
    Briefcase,
    Users,
    GraduationCap,
    Crown,
    Ship,
    Plane,
    FileText
  };

  // Get dynamic LOB colors based on index
  const getLOBColor = index => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-orange-500",
      "bg-purple-500",
      "bg-indigo-500",
      "bg-teal-500",
      "bg-red-500",
      "bg-yellow-500",
      "bg-pink-500",
      "bg-cyan-500",
      "bg-emerald-500",
      "bg-violet-500"
    ];
    return colors[index % colors.length];
  };

  // Get status (for demo purposes, alternating between published/draft)
  const getLOBStatus = index => {
    return index % 2 === 0 ? "published" : "draft";
  };

  // Editor configuration
  const config = {
    readonly: false,
    height: "100%",
    toolbar: true,
    spellcheck: true,
    language: "en",
    toolbarButtonSize: "medium",
    toolbarAdaptive: false,
    showCharsCounter: true,
    showWordsCounter: true,
    showXPathInStatusbar: false,
    askBeforePasteHTML: true,
    askBeforePasteFromWord: true,
    minHeight: "100%",
    maxHeight: "100%",
    buttons: [
      "source",
      "|",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "superscript",
      "subscript",
      "|",
      "ul",
      "ol",
      "|",
      "outdent",
      "indent",
      "|",
      "font",
      "fontsize",
      "brush",
      "paragraph",
      "|",
      "image",
      "link",
      "table",
      "|",
      "align",
      "undo",
      "redo",
      "|",
      "hr",
      "eraser",
      "copyformat",
      "|",
      "fullsize",
      "selectall",
      "print"
    ]
  };

  // Load LOBs on component mount
  useEffect(() => {
    const loadLOBs = async () => {
      try {
        setLoading(true);
        const lobsData = await getLOBs();
        setLobs(lobsData);
        setFilteredLobs(lobsData);
      } catch (error) {
        setNotification({ type: "error", message: "Failed to load LOBs." });
      } finally {
        setLoading(false);
      }
    };
    loadLOBs();
  }, []);

  // Status badge helper
  const getStatusBadge = status => {
    if (status === "published") {
      return "bg-gray-900 text-white text-xs px-3 py-1 rounded-full font-medium";
    } else {
      return "bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded-full font-medium";
    }
  };

  // Get LOB icon component
  const getLOBIconComponent = lobName => {
    const iconName = getLOBIcon(lobName);
    const IconComponent = iconComponents[iconName] || FileText;
    return IconComponent;
  };

  // Convert LOB name to title case with spaces
  const formatLobTitle = lob => {
    if (!lob) return "";
    return lob
      .replace(/_/g, " ")
      .replace(/-/g, " ")
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Remove sidebar search bar, keep only LOB list
  return (
    <div className="fixed inset-0 flex flex-col bg-[#f7f8fa] w-[95%] center mx-auto border border-gray-200 shadow-lg">
      {/* Header - Full width */}
      <header
        className="w-full bg-white px-8 py-4 flex-shrink-0 flex items-center justify-between border-b border-gray-200 shadow-sm"
        style={{ minHeight: 64 }}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center shadow-sm">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div className="flex flex-col justify-center">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight leading-tight mb-0 text-left">
              Interpretation Guides
            </h1>
            <p className="text-sm text-gray-600 mt-0">
              Manage underwriting guidelines for each line of business
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handlePreview}
            className="flex items-center gap-2 px-5 py-2.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-gray-300 rounded-lg transition-all duration-200 font-medium"
            disabled={
              contentLoading ||
              !(typeof content === "string"
                ? content.trim()
                : String(content || "").trim())
            }
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button
            onClick={handleSave}
            disabled={
              saving ||
              contentLoading ||
              !(typeof content === "string"
                ? content.trim()
                : String(content || "").trim())
            }
            className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-200 font-medium shadow-sm"
          >
            {saving ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Guide
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex flex-1 w-full h-full overflow-auto">
        {/* Sidebar - Lines of Business */}
        <aside className="w-80 h-full bg-white flex flex-col border-r border-gray-200 shadow-sm">
          <div className="px-6 pt-6 pb-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900 mb-4 tracking-tight">
              Lines of Business
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full py-8">
                <Loader className="w-8 h-8 animate-spin text-orange-500 mb-3" />
                <p className="text-gray-600 text-sm">Loading LOBs...</p>
              </div>
            ) : (
              <div className="flex flex-col gap-1 px-2">
                {filteredLobs.map((lob, index) => {
                  const lobTitle = formatLobTitle(lob);
                  const color = getLOBColor(index);
                  const status = getLOBStatus(index);
                  const IconComponent = getLOBIconComponent(lob);
                  const isSelected = selectedLob === lob;
                  return (
                    <button
                      key={index}
                      onClick={() => handleLobSelect(lob)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-200 relative text-left ${
                        isSelected
                          ? "bg-orange-50 border-orange-200 shadow ring-2 ring-orange-200"
                          : "hover:bg-gray-50 border-gray-100 hover:border-gray-200"
                      }`}
                      style={
                        isSelected
                          ? { boxShadow: "0 0 0 2px #ea580c inset" }
                          : {}
                      }
                    >
                      <span
                        className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center shadow-sm`}
                      >
                        <IconComponent className="w-5 h-5 text-white" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-semibold text-gray-900 text-sm truncate">
                          {lobTitle}
                        </span>
                        {/* <span className={getStatusBadge(status)}>{status}</span> */}
                      </span>
                      {isSelected && (
                        <span className="absolute right-0 top-0 h-full w-1 bg-orange-500 rounded-r-lg" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col h-full min-w-0 overflow-y-auto">
          {/* Notification */}
          {notification && (
            <div
              className={`fixed left-1/2 top-6 transform -translate-x-1/2 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 ${
                notification.type === "success"
                  ? "bg-green-500 text-white"
                  : notification.type === "error"
                  ? "bg-red-500 text-white"
                  : "bg-blue-500 text-white"
              }`}
              style={{ minWidth: 320, maxWidth: 480, textAlign: "center" }}
            >
              <div className="flex items-center justify-center gap-2">
                {notification.type === "success" ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span>{notification.message}</span>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 flex flex-col min-h-0 overflow-auto px-8 py-8">
            {selectedLob ? (
              <div className="flex flex-col h-full">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-8 py-6 mb-6 flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full mr-2"
                        style={{ background: "#2563eb" }}
                      />
                      {kebabToTitle(selectedLob)} Interpretation Guide
                    </h3>
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <span>
                        Last modified: {new Date().toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-lg">
                    {showPreview ? "👁️ Preview mode" : "✏️ Edit mode"}
                  </span>
                </div>
                <div className="flex-1 flex flex-col">
                  <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6 overflow-auto">
                    {contentLoading ? (
                      <div className="flex flex-col items-center justify-center h-full">
                        <Loader className="w-12 h-12 animate-spin text-orange-500 mb-4" />
                        <span className="text-gray-600 text-lg">
                          Loading content for {kebabToTitle(selectedLob)}...
                        </span>
                      </div>
                    ) : showPreview ? (
                      <div className="h-full">
                        <div className="h-full overflow-y-auto prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-ul:text-gray-700 prose-ol:text-gray-700">
                          <div
                            dangerouslySetInnerHTML={{
                              __html:
                                content ||
                                "<div class='text-center text-gray-500 py-12'><p class='text-lg'>No content available for preview.</p><p class='text-sm mt-2'>Switch to Edit mode to add content.</p></div>"
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="h-full">
                        <JoditEditor
                          ref={editor}
                          value={
                            typeof content === "string"
                              ? content
                              : String(content || "")
                          }
                          config={config}
                          tabIndex={1}
                          onBlur={newContent =>
                            setContent(
                              typeof newContent === "string"
                                ? newContent
                                : String(newContent || "")
                            )
                          }
                          onChange={() => {}}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                    <BookOpen className="w-10 h-10 text-orange-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Select a Line of Business
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Choose a LOB from the sidebar to start viewing or editing
                    its interpretation guide.
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
